import { forwardRef } from 'react';
import { cn } from './cn';

/**
 * <Input>
 *
 * Props:
 *   - prefix:  icono o nodo a la izquierda
 *   - suffix:  icono o nodo a la derecha (ej: badge Ctrl+K)
 *   - error:   string — agrega borde rojo y muestra debajo
 *   - label:   string — render arriba
 *   - hint:    string — gris pequeno debajo
 *
 * Estilo: rounded-lg, focus ring sutil cyan, sin glow.
 * Acepta type="email|password|number|text|search|etc."
 */
const Input = forwardRef(({
    prefix,
    suffix,
    error,
    label,
    hint,
    className,
    wrapperClassName,
    id,
    required,
    ...rest
}, ref) => {
    return (
        <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
            {label && (
                <label htmlFor={id} className="text-xs font-medium text-muted">
                    {label}
                    {required && (
                        <span className="text-danger ml-0.5" aria-hidden="true">*</span>
                    )}
                </label>
            )}
            <div
                className={cn(
                    'flex items-center gap-2 px-3 h-10 rounded-lg',
                    'bg-surface border transition',
                    'focus-within:ring-2 focus-within:ring-cyan2/30',
                    error
                        ? 'border-danger/60 focus-within:border-danger'
                        : 'border-line focus-within:border-cyan2'
                )}
            >
                {prefix && <span className="text-muted shrink-0 inline-flex">{prefix}</span>}
                <input
                    ref={ref}
                    id={id}
                    required={required}
                    className={cn(
                        'flex-1 bg-transparent outline-none text-sm text-text',
                        'placeholder:text-muted/70 disabled:opacity-50',
                        className
                    )}
                    {...rest}
                />
                {suffix && <span className="shrink-0 inline-flex">{suffix}</span>}
            </div>
            {hint && !error && <p className="text-xs text-muted">{hint}</p>}
            {error && <p className="text-xs text-danger">{error}</p>}
        </div>
    );
});

Input.displayName = 'Input';
export default Input;
